import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import medLogo from "../assets/icons/mediately-insights-logo.svg";

export interface PDFGenerationOptions {
  filename: string;
  title?: string;
  subtitle?: string;
  headerInfo?: {
    from?: string;
    until?: string;
    title?: string;
  };
  includeLogo?: boolean;
}

const convertSvgToDataUrl = async (svgPath: string): Promise<string> => {
  try {
    const response = await fetch(svgPath);
    const svgText = await response.text();

    // Create a temporary canvas to convert SVG to PNG
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    return await new Promise((resolve, reject) => {
      img.onload = () => {
        canvas.width = img.width || 364;
        canvas.height = img.height || 41;
        ctx?.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      };

      img.onerror = () => reject(new Error("Failed to load SVG"));

      // Convert SVG to data URL
      const svgBlob = new Blob([svgText], { type: "image/svg+xml;charset=utf-8" });
      const url = URL.createObjectURL(svgBlob);
      img.src = url;
    });
  } catch (error) {
    console.error("Error converting SVG to data URL:", error);
    throw error;
  }
};

const processElement = async (
  selector: string,
  pdf: jsPDF,
  contentWidth: number,
  currentY: number,
  pageHeight: number,
  margin: number
): Promise<number> => {
  const element = document.querySelector(selector) as HTMLElement;

  if (!element) {
    console.warn(`Element not found: ${selector}`);
    return currentY;
  }

  // Capture element as canvas with reduced scale for smaller file size
  const canvas = await html2canvas(element, {
    scale: 1, // Reduced from 2 to decrease file size
    useCORS: true,
    allowTaint: true,
    backgroundColor: "#ffffff",
    logging: false,
  });

  const imgData = canvas.toDataURL("image/jpeg", 0.8); // Use JPEG with 80% quality instead of PNG
  const imgWidth = contentWidth;
  const imgHeight = (canvas.height * imgWidth) / canvas.width;

  let newY = currentY;

  // Check if we need a new page
  if (newY + imgHeight > pageHeight - margin) {
    pdf.addPage();
    newY = margin;
  }

  // Add image to PDF
  pdf.addImage(imgData, "JPEG", margin, newY, imgWidth, imgHeight);
  return newY + imgHeight + 10; // Add spacing between elements
};

export const generatePDFFromElements = async (
  elementSelectors: string[],
  options: PDFGenerationOptions
): Promise<void> => {
  try {
    // eslint-disable-next-line new-cap
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
      compress: true, // Enable PDF compression
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 10;
    const contentWidth = pageWidth - 2 * margin;

    let currentY = margin;

    // Add logo if requested
    if (options.includeLogo) {
      try {
        const logoDataUrl = await convertSvgToDataUrl(medLogo);
        const logoWidth = 80; // Scale down from original 364px
        const logoHeight = 9; // Scale down from original 41px

        pdf.addImage(logoDataUrl, "PNG", margin, currentY, logoWidth, logoHeight);
        currentY += logoHeight + 10; // Add spacing after logo
      } catch (error) {
        console.warn("Could not add logo to PDF:", error);
        // Continue without logo if it fails
      }
    }

    // Add header
    if (options.title) {
      // Adjust font size based on title length for better appearance
      const titleLength = options.title.length;
      let fontSize = 8;
      let lineHeight = 3;

      if (titleLength > 80) {
        fontSize = 6;
        lineHeight = 2.5;
      } else if (titleLength > 60) {
        fontSize = 7;
        lineHeight = 2.8;
      }

      pdf.setFontSize(fontSize);
      pdf.setFont("helvetica", "bold");

      // Create title as canvas image to avoid jsPDF character spacing issues
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      if (ctx) {
        // Configure initial text styling
        let textFontSize = fontSize * 3;
        ctx.font = `bold ${textFontSize}px Helvetica, Arial, sans-serif`;

        // Word wrapping function for canvas
        const wrapText = (text: string, maxWidth: number) => {
          const words = text.split(" ");
          const lines: string[] = [];
          let currentLine = "";

          words.forEach((word) => {
            const testLine = currentLine ? `${currentLine} ${word}` : word;
            const metrics = ctx.measureText(testLine);

            if (metrics.width <= maxWidth || !currentLine) {
              currentLine = testLine;
            } else {
              lines.push(currentLine);
              currentLine = word;
            }
          });

          if (currentLine) {
            lines.push(currentLine);
          }

          return lines;
        };

        // Calculate canvas dimensions with reduced resolution for smaller file size
        const maxCanvasWidth = contentWidth * 2; // Reduced from 4x to 2x
        let titleLines = wrapText(options.title, maxCanvasWidth);

        // If too many lines, reduce font size and recalculate
        while (titleLines.length > 3 && textFontSize > 24) {
          textFontSize -= 6;
          ctx.font = `bold ${textFontSize}px Helvetica, Arial, sans-serif`;
          titleLines = wrapText(options.title, maxCanvasWidth);
        }

        // Set final canvas size based on text
        const canvasLineHeight = textFontSize * 1.2;
        canvas.width = maxCanvasWidth;
        canvas.height = titleLines.length * canvasLineHeight + 10;

        // Reconfigure context after canvas resize
        ctx.fillStyle = "#000000";
        ctx.font = `bold ${textFontSize}px Helvetica, Arial, sans-serif`;
        ctx.textAlign = "left";
        ctx.textBaseline = "top";

        // Draw each line
        titleLines.forEach((line, index) => {
          ctx.fillText(line, 0, index * canvasLineHeight);
        });

        // Convert canvas to data URL and add to PDF with compression
        const titleImageData = canvas.toDataURL("image/jpeg", 0.9); // Use JPEG with 90% quality
        const imgHeight = canvas.height / 2; // Scale back down from 2x resolution

        pdf.addImage(titleImageData, "JPEG", margin, currentY, contentWidth, imgHeight);
        currentY += imgHeight + 6;
      } else {
        // Fallback to regular text if canvas fails
        pdf.text(options.title, margin, currentY);
        currentY += lineHeight + 6;
      }
    }

    if (options.subtitle) {
      pdf.setFontSize(12);
      pdf.setFont("helvetica", "normal");

      // Split text to fit within page width
      const subtitleLines = pdf.splitTextToSize(options.subtitle, contentWidth);
      pdf.text(subtitleLines, margin, currentY);

      // Calculate height based on number of lines
      const lineHeight = 5; // Approximate line height for font size 12
      currentY += subtitleLines.length * lineHeight + 3; // Add extra spacing after subtitle
    }

    // Add period info
    if (options.headerInfo?.from && options.headerInfo?.until) {
      pdf.setFontSize(10);
      pdf.text(`Period: ${options.headerInfo.from} - ${options.headerInfo.until}`, margin, currentY);
      currentY += 8;
    }

    currentY += 5; // Add spacing

    // Process elements sequentially
    await elementSelectors.reduce(async (yPromise, selector) => {
      const y = await yPromise;
      return processElement(selector, pdf, contentWidth, y, pageHeight, margin);
    }, Promise.resolve(currentY));

    // Save the PDF
    pdf.save(options.filename);
  } catch (error) {
    console.error("Error generating PDF:", error);
    throw new Error("Failed to generate PDF");
  }
};

export const generateReportPDF = async (reportData: {
  title: string;
  period: { from: string; until: string };
  type: string;
  periodOption?: string;
}): Promise<void> => {
  const filename = `${reportData.type}_report_${reportData.period.from}_${reportData.period.until}.pdf`;

  // Define selectors for report elements
  const elementSelectors = [
    '[data-pdf_report="report-chart"]', // Chart container
    '[data-pdf_report="report-period-table"]', // Period table
    '[data-pdf_report="report-specializations-table"]', // Specializations table
  ];

  await generatePDFFromElements(elementSelectors, {
    filename,
    title: reportData.title,
    subtitle: `${reportData.type.toUpperCase()} Report`,
    headerInfo: {
      from: reportData.period.from,
      until: reportData.period.until,
    },
    includeLogo: true,
  });
};
